/**
 * AlertInbox
 *
 * Painel/dropdown lateral com histórico de alertas. Acionado por ícone de
 * sininho na top da aba Canais. Mostra feed cronológico, mark-as-read,
 * filtros e CTAs por alerta.
 */

import React, { useState } from 'react';
import { IconBell, IconBellRinging, IconX, IconCheck } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { useChannelAlerts } from '../../contexts/ChannelAlertsContext';
import {
  SEVERITY_THEME,
  EVENT_ICONS,
  EVENT_LABELS,
  effectiveSeverity,
  resolveAlertCTA,
} from './alertConfig';

function formatRelativeTime(timestampMs) {
  const diff = Date.now() - timestampMs;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(timestampMs).toLocaleDateString('pt-BR');
}

const AlertItem = ({ alert, onAction, onMarkRead, onDismiss }) => {
  const severity = effectiveSeverity(alert);
  const theme = SEVERITY_THEME[severity] || SEVERITY_THEME.info;
  const Icon = EVENT_ICONS[alert.event];
  const cta = resolveAlertCTA(alert);
  const label = EVENT_LABELS[alert.event] || alert.event;

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: 12,
        borderBottom: '1px solid #f3f4f6',
        background: alert.read ? '#fff' : '#fafbff',
        cursor: 'pointer',
      }}
      onClick={() => !alert.read && onMarkRead(alert.id)}
    >
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: 8,
          background: theme.bg,
          color: theme.iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {Icon && <Icon size={16} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: theme.color }}>
            {label}
          </span>
          <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
            {formatRelativeTime(alert.createdAt)}
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.4, marginBottom: cta ? 6 : 0 }}>
          {alert.message}
        </div>
        {cta && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAction(cta.action, alert);
              onMarkRead(alert.id);
            }}
            style={{
              padding: '4px 10px',
              border: `1px solid ${theme.iconColor}`,
              background: theme.bg,
              color: theme.color,
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {cta.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(alert.id);
        }}
        aria-label="Remover"
        style={{
          padding: 4,
          border: 'none',
          background: 'transparent',
          color: '#9ca3af',
          cursor: 'pointer',
          alignSelf: 'flex-start',
        }}
      >
        <IconX size={12} />
      </button>
    </div>
  );
};

const AlertInbox = ({ onAction, filters = {} }) => {
  const { alerts, unreadCount, criticalCount, markRead, markAllRead, dismiss, clear } =
    useChannelAlerts(filters);
  const [open, setOpen] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState('all');

  const filtered = alerts.filter(
    (a) => filterSeverity === 'all' || effectiveSeverity(a) === filterSeverity,
  );

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Alertas (${unreadCount} não lidos)`}
        title="Alertas"
        style={{
          position: 'relative',
          padding: 8,
          border: '1px solid #e5e7eb',
          background: '#fff',
          borderRadius: 8,
          cursor: 'pointer',
          color: '#374151',
        }}
      >
        {unreadCount > 0 ? <IconBellRinging size={18} /> : <IconBell size={18} />}
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 8,
              background: criticalCount > 0 ? '#dc2626' : '#3b82f6',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop para fechar ao clicar fora */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1000,
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              width: 380,
              maxHeight: 500,
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h6 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                Alertas {unreadCount > 0 ? `· ${unreadCount}` : ''}
              </h6>
              <div style={{ display: 'flex', gap: 6 }}>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={markAllRead}
                    title="Marcar todos como lido"
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      borderRadius: 6,
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    <IconCheck size={12} /> Lido
                  </button>
                )}
                {alerts.length > 0 && (
                  <button
                    type="button"
                    onClick={async () => {
                      const r = await Swal.fire({
                        title: 'Limpar todos os alertas?',
                        text: 'O histórico será removido. Não afeta alertas no servidor.',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#dc2626',
                        confirmButtonText: 'Limpar',
                        cancelButtonText: 'Cancelar',
                      });
                      if (r.isConfirmed) clear();
                    }}
                    title="Limpar todos"
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      borderRadius: 6,
                      fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    Limpar
                  </button>
                )}
              </div>
            </div>

            <div
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                gap: 6,
              }}
            >
              {['all', 'error', 'warning', 'info'].map((sev) => (
                <button
                  key={sev}
                  type="button"
                  onClick={() => setFilterSeverity(sev)}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid',
                    borderColor: filterSeverity === sev ? '#3b82f6' : '#e5e7eb',
                    background: filterSeverity === sev ? '#eff6ff' : '#fff',
                    color: filterSeverity === sev ? '#1e40af' : '#6b7280',
                    borderRadius: 16,
                    fontSize: 11,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {sev === 'all' ? 'Todos' : SEVERITY_THEME[sev]?.label || sev}
                </button>
              ))}
            </div>

            <div style={{ overflow: 'auto', flex: 1 }}>
              {filtered.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                  Sem alertas {filterSeverity !== 'all' ? 'desta categoria' : ''}.
                </div>
              ) : (
                filtered.map((alert) => (
                  <AlertItem
                    key={alert.id}
                    alert={alert}
                    onAction={(action, a) => {
                      if (onAction) onAction(action, a);
                      setOpen(false);
                    }}
                    onMarkRead={markRead}
                    onDismiss={dismiss}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AlertInbox;
